import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import TenantAvatar from "./TenantAvatar.tsx";
import UserAvatar from "./UserAvatar.tsx";

interface IdentityCellProps {
  /** An avatar, logo or `LogoBadge`. */
  avatar?: ReactNode;
  name: ReactNode;
  /** A small outlined chip after the name ("You", "Primary"). */
  tag?: ReactNode;
  /** The second line: an email, a slug, a short description. */
  caption?: ReactNode;
  /** Makes the name a link. */
  to?: string;
}

/** Who or what a row is: avatar, bold name, an optional tag and a caption line. */
export default function IdentityCell({ avatar, name, tag, caption, to }: IdentityCellProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
      {avatar}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {to ? (
              <Link component={RouterLink} to={to} underline="hover" color="inherit">
                {name}
              </Link>
            ) : (
              name
            )}
          </Typography>
          {tag != null &&
            (typeof tag === "string" ? (
              <Chip label={tag} size="small" variant="outlined" sx={{ height: 18 }} />
            ) : (
              tag
            ))}
        </Box>
        {caption != null && (
          <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
            {caption}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface UserCellProps {
  displayName: string | null | undefined;
  email: string | null | undefined;
  pictureUrl?: string | null;
  tag?: ReactNode;
  to?: string;
}

/** A person: avatar, their name (or email when they have none) and their email under it. */
export function UserCell({ displayName, email, pictureUrl, tag, to }: UserCellProps) {
  return (
    <IdentityCell
      avatar={<UserAvatar displayName={displayName} email={email} pictureUrl={pictureUrl} tooltip={false} />}
      name={displayName || email || "Unknown"}
      caption={displayName ? email : undefined}
      tag={tag}
      to={to}
    />
  );
}

interface TenantCellProps {
  name: string;
  logoUrl?: string | null;
  caption?: ReactNode;
  tag?: ReactNode;
  to?: string;
}

/** A tenant: its logo (or initials), name and an optional caption. */
export function TenantCell({ name, logoUrl, caption, tag, to }: TenantCellProps) {
  return (
    <IdentityCell
      avatar={<TenantAvatar name={name} logoUrl={logoUrl} />}
      name={name}
      caption={caption}
      tag={tag}
      to={to}
    />
  );
}
